import HiddenInput from "@/components/form/components/HiddenInput";
import { ItemCreatedUpdatedInfoView } from "@/components/views/ItemCreatedUpdatedInfoView";
import { selectStandardProps } from "@/utils/componentUtils";
import { setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Flex, Form, Input, Modal, Select } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCreateProductGroup, useUpdateProductGroup } from "../../../../common/api/queries";
import { ModalSizes, rowGutter } from "../../../../common/constants";
import { TopAgentType } from "../../../../common/enums";
import { useCurrentAgentAccount } from "../../../auth/hooks";
import { ProductGroupCategory } from "../../enums";
import { CreateUpdateProductGroup, ProductGroup } from "../../types";
import { convertProductGroupToCreateUpdateObject } from "../../utils";

interface Props {
  open: boolean;
  onFormCancel: VoidFunction;
  productGroup?: ProductGroup;
}

const ProductGroupForm = ({ open, onFormCancel, productGroup }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<CreateUpdateProductGroup>();

  const userAccount = useCurrentAgentAccount();

  const createProductGroup = useCreateProductGroup();
  const updateProductGroup = useUpdateProductGroup();

  useEffect(() => {
    if (open && productGroup) {
      const formData = convertProductGroupToCreateUpdateObject(productGroup);

      form.setFieldsValue(formData);
    }
  }, [open, productGroup, form]);

  const handleFormSubmit = () => {
    form.validateFields().then((values) => {
      if (productGroup) {
        updateProductGroup.mutate(
          { id: productGroup.id, object: values },
          { onSuccess: onFormCancel, onError: (error) => setErrorsToForm(form, "product.attrs", error.violations) },
        );
      } else {
        createProductGroup.mutate(values, {
          onSuccess: onFormCancel,
          onError: (error) => setErrorsToForm(form, "product.attrs", error.violations),
        });
      }
    });
  };

  return (
    <Modal
      width={ModalSizes.MEDIUM}
      open={open}
      title={t(productGroup ? "product.titles.updateProductGroup" : "product.titles.createProductGroup")}
      cancelText={t("common.cancel")}
      okText={t("common.save")}
      confirmLoading={createProductGroup.isPending || updateProductGroup.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <ItemCreatedUpdatedInfoView className="margin-bottom-small" item={productGroup} />

      <Form form={form} layout="vertical" name="productGroupForm">
        <HiddenInput name="optimisticLockVersion" />

        <Flex gap={rowGutter}>
          <Form.Item
            style={{ flex: 1 }}
            name="name"
            label={t("product.attrs.name")}
            rules={[validations.notBlank, validations.size(1, 64)]}
          >
            <Input />
          </Form.Item>

          {userAccount?.agent?.topAgentType === TopAgentType.PLUS ? (
            <Form.Item style={{ flex: 1 }} name="category" label={t("product.enums.productGroupCategory._label")}>
              <Select
                {...selectStandardProps}
                allowClear
                options={Object.keys(ProductGroupCategory).map((category) => ({
                  value: category,
                  label: t("product.enums.productGroupCategory." + category),
                }))}
              />
            </Form.Item>
          ) : undefined}
        </Flex>
      </Form>
    </Modal>
  );
};

export default ProductGroupForm;
